"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import { createMagazineFromUpload } from "@/app/admin/magazines/actions";
import { uploadMagazineToCache } from "@/lib/magazine-cache-upload";
import {
  deleteQueuedUpload,
  fileFromRecord,
  listQueuedUploads,
  putQueuedUpload,
} from "@/lib/upload-queue-store";
import { titleFromFileName } from "@/lib/video-title";
import { uploadWithProgress } from "@/lib/upload-with-progress";

export type BulkMagazineMeta = {
  category: string;
  issue?: string;
  year?: number;
  month?: number;
  description?: string;
};

export type MagazineUploadItem = {
  id: string;
  fileName: string;
  phase: "queued" | "presigning" | "uploading" | "creating" | "done" | "error";
  loaded: number;
  total: number;
  storageKey?: string;
  magazineId?: string;
  errorMessage?: string;
};

type QueueJob = {
  id: string;
  file: File;
  meta: BulkMagazineMeta;
};

type MagazineUploadContextValue = {
  uploads: MagazineUploadItem[];
  enqueueUploads: (files: File[], meta: BulkMagazineMeta) => void;
  cancelUpload: (id: string) => void;
  dismissUpload: (id: string) => void;
  clearCompleted: () => void;
};

const MagazineUploadContext = createContext<MagazineUploadContextValue | null>(
  null,
);

export function useMagazineUploads() {
  const ctx = useContext(MagazineUploadContext);
  if (!ctx) {
    throw new Error("useMagazineUploads must be used within MagazineUploadProvider");
  }
  return ctx;
}

function newUploadId(): string {
  return crypto.randomUUID();
}

export function MagazineUploadProvider({
  children,
  userId,
}: {
  children: ReactNode;
  userId?: string | null;
}) {
  const router = useRouter();
  const [uploads, setUploads] = useState<MagazineUploadItem[]>([]);
  const abortControllers = useRef(new Map<string, AbortController>());
  const queueRef = useRef<QueueJob[]>([]);
  const activeIdRef = useRef<string | null>(null);
  const batchPending = useRef(0);
  const restoredUserId = useRef<string | null>(null);

  const patchUpload = useCallback(
    (id: string, patch: Partial<MagazineUploadItem>) => {
      setUploads((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
      );
    },
    [],
  );

  const maybeRefresh = useCallback(() => {
    batchPending.current -= 1;
    if (batchPending.current <= 0) {
      batchPending.current = 0;
      router.refresh();
    }
  }, [router]);

  const dropStored = useCallback((id: string) => {
    void deleteQueuedUpload(id);
  }, []);

  const runPipeline = useCallback(
    async (id: string, file: File, meta: BulkMagazineMeta) => {
      const controller = new AbortController();
      abortControllers.current.set(id, controller);

      try {
        patchUpload(id, { phase: "presigning", total: file.size });

        const presignRes = await fetch("/api/admin/presign-magazine-upload", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fileName: file.name,
            contentType: file.type || "application/pdf",
          }),
          signal: controller.signal,
        });

        const data = (await presignRes.json()) as {
          uploadUrl?: string;
          storageKey?: string;
          error?: string;
        };

        if (!presignRes.ok) {
          throw new Error(data.error ?? "Could not start upload");
        }
        if (!data.uploadUrl || !data.storageKey) {
          throw new Error("Bad response from server");
        }

        patchUpload(id, {
          phase: "uploading",
          loaded: 0,
          total: file.size,
          storageKey: data.storageKey,
        });

        await uploadWithProgress(
          data.uploadUrl,
          file,
          file.type || "application/pdf",
          (loaded, total) => {
            patchUpload(id, { phase: "uploading", loaded, total });
          },
          controller.signal,
        );

        patchUpload(id, {
          phase: "creating",
          loaded: file.size,
          total: file.size,
        });

        await uploadMagazineToCache(data.storageKey, file);

        const { id: magazineId } = await createMagazineFromUpload({
          title: titleFromFileName(file.name),
          category: meta.category,
          storageKey: data.storageKey,
          issue: meta.issue,
          year: meta.year,
          month: meta.month,
          description: meta.description,
        });

        patchUpload(id, {
          phase: "done",
          magazineId,
          loaded: file.size,
          total: file.size,
        });
        dropStored(id);
      } catch (e) {
        if (controller.signal.aborted) {
          dropStored(id);
          setUploads((prev) => prev.filter((item) => item.id !== id));
        } else {
          dropStored(id);
          const message = e instanceof Error ? e.message : "Upload failed";
          patchUpload(id, { phase: "error", errorMessage: message });
        }
      } finally {
        abortControllers.current.delete(id);
        maybeRefresh();
      }
    },
    [dropStored, maybeRefresh, patchUpload],
  );

  const pump = useCallback(() => {
    if (activeIdRef.current) return;
    const next = queueRef.current.shift();
    if (!next) return;
    activeIdRef.current = next.id;
    void (async () => {
      try {
        await runPipeline(next.id, next.file, next.meta);
      } finally {
        activeIdRef.current = null;
        pump();
      }
    })();
  }, [runPipeline]);

  useEffect(() => {
    if (!userId) return;
    if (restoredUserId.current === userId) return;
    let cancelled = false;

    void (async () => {
      const records = await listQueuedUploads(userId, "magazine");
      if (cancelled) return;
      restoredUserId.current = userId;
      if (records.length === 0) return;

      const restoredItems: MagazineUploadItem[] = [];
      const jobs: QueueJob[] = [];

      for (const record of records) {
        const file = fileFromRecord(record);
        const meta = (record.meta ?? { category: "" }) as BulkMagazineMeta;
        restoredItems.push({
          id: record.id,
          fileName: record.fileName,
          phase: "queued",
          loaded: 0,
          total: record.size,
        });
        jobs.push({ id: record.id, file, meta });
      }

      batchPending.current += restoredItems.length;
      queueRef.current.push(...jobs);
      setUploads((prev) => {
        const existing = new Set(prev.map((item) => item.id));
        const extra = restoredItems.filter((item) => !existing.has(item.id));
        return extra.length === 0 ? prev : [...prev, ...extra];
      });
      pump();
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, pump]);

  const enqueueUploads = useCallback(
    (files: File[], meta: BulkMagazineMeta) => {
      if (files.length === 0) return;

      batchPending.current += files.length;
      let order = Date.now();

      const newItems: MagazineUploadItem[] = [];
      for (const file of files) {
        const id = newUploadId();
        queueRef.current.push({ id, file, meta });
        newItems.push({
          id,
          fileName: file.name,
          phase: "queued",
          loaded: 0,
          total: file.size,
        });
        if (userId) {
          void putQueuedUpload({
            id,
            userId,
            kind: "magazine",
            file,
            fileName: file.name,
            type: file.type || "application/pdf",
            size: file.size,
            meta,
            phase: "queued",
            order: order++,
          });
        }
      }

      setUploads((prev) => [...prev, ...newItems]);
      pump();
    },
    [pump, userId],
  );

  const cancelUpload = useCallback(
    (id: string) => {
      const queuedIndex = queueRef.current.findIndex((job) => job.id === id);
      if (queuedIndex >= 0) {
        queueRef.current.splice(queuedIndex, 1);
        dropStored(id);
        setUploads((prev) => prev.filter((item) => item.id !== id));
        maybeRefresh();
        return;
      }

      const controller = abortControllers.current.get(id);
      if (controller) {
        controller.abort();
      }
    },
    [dropStored, maybeRefresh],
  );

  const dismissUpload = useCallback(
    (id: string) => {
      dropStored(id);
      setUploads((prev) => prev.filter((item) => item.id !== id));
    },
    [dropStored],
  );

  const clearCompleted = useCallback(() => {
    setUploads((prev) =>
      prev.filter((item) => item.phase !== "done" && item.phase !== "error"),
    );
  }, []);

  return (
    <MagazineUploadContext.Provider
      value={{
        uploads,
        enqueueUploads,
        cancelUpload,
        dismissUpload,
        clearCompleted,
      }}
    >
      {children}
    </MagazineUploadContext.Provider>
  );
}
